'use client';

import { useAIHumanITUsage } from '@/lib/use-aihumanit-usage';
import { Zap, Crown } from 'lucide-react';
import { Session } from 'next-auth';

interface UsageDisplayProps {
    session: Session;
    showUpgrade?: boolean;
    textOnly?: boolean;
}

export function UsageDisplay({ session, showUpgrade = true, textOnly = false }: UsageDisplayProps) {
    const siteId = (session as any)?.siteId || '87c2d73e-7030-4048-9311-0eb6f66d1e60';
    const { usage, loading, error } = useAIHumanITUsage(siteId);

    const handleClick = () => {
        // TODO: Open usage modal with detailed info and upgrade options
        console.log('Open usage modal', { usage, isOverLimit: usage && usage.percentageUsed >= 100 });
    };

    if (loading) {
        return textOnly ? (
            <span className="animate-pulse">...</span>
        ) : (
            <div className="h-6 flex items-center">
                <div className="text-xs text-gray-500 animate-pulse">Loading...</div>
            </div>
        );
    }

    if (error || !usage) {
        return null;
    }

    // Determine if user has unlimited plan (subscription with high credits or no subscription with free tier)
    const hasSubscription = usage.subscription !== null;
    const subscriptionCredits = usage.subscription?.credits ?? 0;
    const isUnlimited = hasSubscription && (subscriptionCredits < 0 || subscriptionCredits >= 999999);
    const currentWords = usage.currentMonth.totalWordsBilled;
    
    // For free tier users, limit is 1000 words per month
    const limit = hasSubscription ? subscriptionCredits : 1000;
    const percentage = usage.percentageUsed;

    // Determine tier name
    const getTierInfo = () => {
        if (isUnlimited) return { name: 'Unlimited', color: 'text-yellow-600' };
        if (hasSubscription) return { name: 'Pro', color: 'text-blue-600' };
        return { name: 'Free', color: 'text-gray-600' };
    };

    const tierInfo = getTierInfo();

    if (isUnlimited) {
        if (textOnly) {
            return (
                <div className="text-right animate-in fade-in duration-300">
                    <div className={`font-medium text-xs ${tierInfo.color}`}>
                        {tierInfo.name}
                    </div>
                    <div className="text-xs text-gray-500">No limit</div>
                </div>
            );
        }
        return (
            <div 
                className="group cursor-pointer animate-in fade-in duration-300"
                onClick={handleClick}
                title="Unlimited plan - no word limits"
            >
                <div className="flex items-center space-x-1">
                    <Crown className="w-3 h-3 text-yellow-500" />
                    <span className="text-xs font-medium text-yellow-600">Unlimited</span>
                </div>
            </div>
        );
    }

    const isNearLimit = percentage >= 80;
    const isOverLimit = percentage >= 100;
    const needsUpgrade = isNearLimit || isOverLimit || usage.upgradeRecommended;

    if (textOnly) {
        const wordsLeft = limit - currentWords;
        
        return (
            <div className="text-right animate-in fade-in duration-300">
                <div className={`font-medium text-xs ${tierInfo.color}`}>
                    {tierInfo.name}
                </div>
                <div className={`text-xs ${isOverLimit ? 'text-red-500' : isNearLimit ? 'text-orange-500' : 'text-gray-500'}`}>
                    {isOverLimit ? `Over by ${Math.abs(wordsLeft).toLocaleString()}` : `${wordsLeft.toLocaleString()} left`}
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center space-x-2 animate-in slide-in-from-top-2 fade-in duration-300">
            {/* Progress bar */}
            <div 
                className="group cursor-pointer relative"
                onClick={handleClick}
                title={`${currentWords.toLocaleString()} / ${limit.toLocaleString()} words used ${isOverLimit ? '- Over limit!' : ''}`}
            >
                <div className="w-20 bg-gray-200 rounded-full h-2 transition-all duration-200 group-hover:h-2.5">
                    <div 
                        className={`h-full rounded-full transition-all duration-300 ${
                            isOverLimit ? 'bg-red-500' : 
                            isNearLimit ? 'bg-yellow-500' : 
                            'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                    />
                </div>
                
                {/* Hover tooltip */}
                <div className="opacity-0 group-hover:opacity-100 absolute z-10 bg-gray-900 text-white text-xs px-2 py-1 rounded mt-1 whitespace-nowrap pointer-events-none transition-opacity">
                    {isOverLimit ? (
                        <span className="text-red-300">Over limit by {(currentWords - limit).toLocaleString()} words</span>
                    ) : (
                        <span>{(limit - currentWords).toLocaleString()} words remaining</span>
                    )}
                </div>
            </div>
            
            {/* Upgrade button when needed */}
            {needsUpgrade && showUpgrade && (
                <button 
                    onClick={(e) => {
                        e.stopPropagation();
                        handleClick();
                    }}
                    className="flex items-center space-x-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1 rounded-full transition-colors"
                    title="Upgrade your plan"
                >
                    <Zap className="w-3 h-3" />
                    <span>Upgrade</span>
                </button>
            )}
        </div>
    );
}
